import React, { useMemo, useRef, useState } from "react";
import { ScanBarcode, Plus } from "lucide-react";
import { formatGs } from "@/lib/format";

export const ProductSearchBar = ({ products, location, onPick, onNew, inputRef }) => {
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const [open, setOpen] = useState(false);
  const localRef = useRef(null);
  const ref = inputRef || localRef;

  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return products.filter((p) => !p.is_combo && (p.name.toLowerCase().includes(t) || (p.barcode || "").includes(t) || (p.category || "").toLowerCase().includes(t))).slice(0, 8);
  }, [q, products]);

  const reset = () => { setQ(""); setHi(0); setOpen(false); ref.current?.focus(); };
  const pick = (p) => { onPick(p); reset(); };
  const createNew = () => { if (q.trim()) { onNew(q.trim()); reset(); } };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Escape") { setOpen(false); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const code = q.trim();
      const exact = products.find((p) => !p.is_combo && p.barcode && p.barcode === code);
      if (exact) return pick(exact);
      if (results[hi]) return pick(results[hi]);
      if (hi === results.length) createNew();
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2 h-12 px-3 rounded-xl bg-secondary border-2 border-primary/40 focus-within:border-primary transition-colors">
        <ScanBarcode className="w-5 h-5 text-primary shrink-0" />
        <input ref={ref} autoFocus value={q} data-testid="purchase-search"
          onChange={(e) => { setQ(e.target.value); setHi(0); setOpen(true); }} onKeyDown={onKey} onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Escaneá el código de barras o buscá por nombre / categoría… (Enter para agregar)"
          className="flex-1 bg-transparent text-sm focus:outline-none" />
      </div>
      {open && q.trim() && (
        <div className="absolute z-20 left-0 right-0 mt-1 card-surface p-1 shadow-xl max-h-72 overflow-y-auto" data-testid="purchase-search-results">
          {results.map((p, i) => (
            <button key={p.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(p)} data-testid={`purchase-search-item-${p.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm ${i === hi ? "bg-primary/15" : "hover:bg-accent"}`}>
              {p.image_url ? <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover" /> : <div className="w-8 h-8 rounded bg-accent" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{p.name}</div>
                <div className="text-xs text-muted-foreground font-data">{p.barcode || "sin código"} · {p.category}</div>
              </div>
              <div className="text-right text-xs">
                <div className="font-data">Stock {p[`stock_${location}`] || 0}</div>
                <div className="text-muted-foreground font-data">Costo {formatGs(p.cost || 0)}</div>
              </div>
            </button>
          ))}
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={createNew} data-testid="purchase-search-new"
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm text-primary ${hi === results.length ? "bg-primary/15" : "hover:bg-accent"}`}>
            <Plus className="w-4 h-4" /> Crear producto nuevo «{q.trim()}»
          </button>
        </div>
      )}
    </div>
  );
};
